import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../api/client';
import { toMiamiDateString } from '../utils/dateUtils';
import { buildCal, addMonths, monthLabel, dow, DAY } from '../ProductionPlanPage';
import { packetChain, periodCell, periodSummary } from '../utils/packetsNeed';
import '../ProductionPlanPage.css';
import './PacketsNeedTab.css';

// Daily Planning → Packets Need: how many small packets the planned sets (and kits) take per day,
// collapsible into ISO weeks and months; on top are the totals, the batches to produce and the operator's shifts.
// Data: GET /plantoproduce/packets-need (sparse maps); the math lives in utils/packetsNeed.js.
// Formulas: server/docs/report-small-packets-need.md

const STORAGE_KEY = 'pn_view';
const FIXED = [['name', 'Packet', 220], ['sku', 'SKU', 110], ['stock', 'Stock', 70]];
const ROW_H = 28;   // = --pp-h of .pp-table: sticky offset of the summary rows under the 3 header rows
const EMPTY = {};

const num = x => Number(x) || 0;
const fmt = n => Math.round(n).toLocaleString('en-US');
const toggle = (arr, x) => (arr.includes(x) ? arr.filter(y => y !== x) : [...arr, x]);
const errMsg = (err, fallback) => (err.response && err.response.data && err.response.data.error) || fallback;

// Flat column list (drives <colgroup> and the cells) + 3 header rows. Every period column carries its own
// days: a month, an ISO-week segment ('W40*' = the part of the week inside the month) or a single day.
// The total of an expanded week / month is its LAST column: in the Balance view it is the balance at the
// end of the period, and at the end is where it reads as such.
function buildModel(cal, expandedMonths, collapsedWeeks, today, workDays, balance) {
  const tot = balance ? 'End' : 'Σ';
  const totTip = what => (balance ? `Balance at the end of ${what} (its last day)` : `Total of ${what}`);
  const cols = [], r1 = [], r2 = [], r3 = [];
  for (const [k, label, w] of FIXED) {
    cols.push({ k, w });
    r1.push({ key: k, label, rowSpan: 3, cls: k === 'name' ? 'pp-name' : '' });
  }
  cal.months.forEach(({ m, segs }, mi) => {
    const dates = segs.flatMap(s => s.dates);
    const ms = 'pp-ms' + (mi % 2 ? ' pp-mo' : '');
    if (!expandedMonths.includes(m)) {
      cols.push({ k: 'p', dates, w: 84, cls: 'pp-ms' });
      r1.push({ key: m, label: '▸ ' + monthLabel(m), rowSpan: 3, cls: ms, act: { type: 'month', m }, title: 'Expand month' });
      return;
    }
    let span = 1;
    const c0 = cols.length, h2 = r2.length, h3 = r3.length;
    for (const s of segs) {
      const wk = `${m}|${s.week}`, act = { type: 'week', week: s.week }, collapsed = collapsedWeeks.includes(s.week);
      const part = s.partial ? ` (partial: ${s.dates[0].slice(5)} to ${s.dates[s.dates.length - 1].slice(5)})` : '';
      const title = `${s.week}${part}. Click to ${collapsed ? 'expand' : 'collapse'}`;
      if (collapsed) {
        cols.push({ k: 'p', dates: s.dates, w: 72 });
        r2.push({ key: wk, label: `▸ ${s.label}`, rowSpan: 2, act, title });
        span += 1;
        continue;
      }
      r2.push({ key: wk, label: `▾ ${s.label}`, colSpan: 1 + s.dates.length, act, title });
      for (const d of s.dates) {
        const off = !workDays.includes(dow(d));   // a day off is greyed like a Sunday of the Production Plan
        const cls = (off ? 'pp-sun' : '') + (d === today ? ' pp-today' : '');
        cols.push({ k: 'p', dates: [d], w: 60, cls });
        r3.push({ key: d, label: `${DAY[dow(d)]} ${Number(d.slice(8))}`, cls, title: off ? `${d}, day off` : d });
      }
      cols.push({ k: 'p', dates: s.dates, w: 72, cls: 'pn-tot' });
      r3.push({ key: wk, label: tot, cls: 'pn-tot', title: totTip(s.week + part) });
      span += 1 + s.dates.length;
    }
    cols.push({ k: 'p', dates, w: 84, cls: 'pn-tot' });
    r2.push({ key: m, label: tot, rowSpan: 2, cls: 'pn-tot', title: totTip(monthLabel(m)) });
    // month-start border on the first column of the month (and on its first day when the first week is open)
    const mark = o => { o.cls = ('pp-ms ' + (o.cls || '')).trim(); };
    mark(cols[c0]); mark(r2[h2]);
    if (!collapsedWeeks.includes(segs[0].week)) mark(r3[h3]);
    r1.push({ key: m, label: '▾ ' + monthLabel(m), colSpan: span, cls: ms, act: { type: 'month', m }, title: 'Collapse month' });
  });
  return { cols, rows: [r1, r2, r3], width: cols.reduce((s, c) => s + c.w, 0) };
}

const defaultView = curYM => ({ to: addMonths(curYM, 2), expandedMonths: [curYM], collapsedWeeks: [], listCollapsed: false, balance: false, showAll: false });
function loadView(curYM) {
  const def = defaultView(curYM);
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!s || typeof s !== 'object') return def;
    const v = { ...def, ...s };
    if (typeof v.to !== 'string' || v.to < curYM || v.to > addMonths(curYM, 23)) v.to = def.to;
    for (const k of ['expandedMonths', 'collapsedWeeks']) if (!Array.isArray(v[k])) v[k] = def[k];
    for (const k of ['listCollapsed', 'balance', 'showAll']) v[k] = v[k] === true;
    return v;
  } catch { return def; }
}

// ── Packet row ──
const Row = React.memo(function Row({ r, cols, balance, settings }) {
  return (
    <tr>
      {cols.map((c, i) => {
        if (c.k === 'name') return <td key={i} className="pp-name" title={r.p.name}>{r.p.name}</td>;
        if (c.k === 'sku') return <td key={i}>{r.p.SKU}</td>;
        if (c.k === 'stock') return <td key={i}>{fmt(Math.max(r.p.stock, 0))}</td>;
        const x = periodCell(r, c.dates, settings);
        return (
          <td key={i} className={(c.cls || '') + (x.lv ? ' pn-' + x.lv : '')} title={`Need ${fmt(x.need)} · balance at the end ${fmt(x.bal)}` + (x.produce ? ` · to produce ${fmt(x.produce)}` : '')}>
            {balance ? fmt(x.bal) : x.need ? fmt(x.need) : '—'}
          </td>
        );
      })}
    </tr>
  );
});

// ── Summary rows (sticky under the header) ──
// Need view: every row is about the period of the column (a day, a week, a month).
// Balance view: the cells show where we are at the END of the period, so the summary does too: it is
// counted from today to the end of the column (SUM_ROWS_TOTAL). The last day of a month, its last week
// and the month itself then show the same numbers, like the balance does.
const SUM_ROWS = [
  ['need', 'Need, pcs', 'Packets the planned sets and kits take in the period (all packets in the list)'],
  ['short', 'SKUs short', 'Packets that are in the plan (taken at least once from today to the end of the period) and end the period below the red limit: the red cells of the column'],
  ['produce', 'To produce, pcs', 'Batches for the packets that drop below the red limit on a day they are taken: a whole number of the packet\'s own batches (3,000 or 6,000), enough to cover what is below zero, never rounded down. Counted once, on the day the packet drops below the limit'],
  ['planned', 'Already planned, pcs', 'Packet production already scheduled for the period (Planned / In production)'],
  ['shiftsShort', 'Shifts · day to 3 PM', '(To produce + Already planned) ÷ packets per day when the operator works until 3 PM. Yellow: more than the working days of the period, ask to work longer'],
  ['shiftsLong', 'Shifts · day to 6 PM', '(To produce + Already planned) ÷ packets per day when the operator works until 6 PM. Red: more than the working days of the period, one operator is not enough'],
];
const SUM_ROWS_TOTAL = [
  ['need', 'Need from today, pcs', 'Packets the planned sets and kits take from today to the end of the period'],
  SUM_ROWS[1],
  ['produce', 'To produce from today, pcs', 'All the batches suggested from today to the end of the period. The same batches as in the Need view, added up day after day'],
  ['planned', 'Planned from today, pcs', 'Packet production scheduled from today to the end of the period (Planned / In production)'],
  ['shiftsShort', 'Shifts from today · day to 3 PM', '(To produce + Planned, from today) ÷ packets per day when the operator works until 3 PM. Yellow: more than the working days from today to the end of the period, ask to work longer'],
  ['shiftsLong', 'Shifts from today · day to 6 PM', '(To produce + Planned, from today) ÷ packets per day when the operator works until 6 PM. Red: more than the working days from today to the end of the period, one operator is not enough'],
];
function sumCell(kind, s, settings, fromToday) {
  if (kind === 'need') return { text: s.need ? fmt(s.need) : '—' };
  if (kind === 'short') return { text: String(s.short), cls: s.short > 0 ? 'pn-alert' : '' };
  if (kind === 'produce' || kind === 'planned') return { text: s[kind] ? fmt(s[kind]) : '—' };
  const long = kind === 'shiftsLong';
  return {
    text: s.load ? s[kind].toFixed(2) : '—',
    cls: long ? (s.overload ? 'pn-alert' : '') : (s.longer ? 'pn-warn' : ''),
    title: `${fmt(s.load)} ÷ ${fmt(long ? settings.capLong : settings.capShort)} · ${s.workdays} working day(s) ${fromToday ? 'from today to the end of the period' : 'in the period'}`,
  };
}

// ── Settings modal ──
// [key, label, tooltip, note under the field]
const FIELDS = [
  [['capShort', 'Packets per day, works until 3 PM', 'How many packets the operator makes in a day that ends at 3 PM'],
   ['capLong', 'Packets per day, works until 6 PM', 'How many packets the operator makes in a day that ends at 6 PM']],
  [['batchSmall', 'Small batch', 'The batch of a packet whose month need is at or below the limit. A bigger shortage takes a whole number of such batches, never rounded down', 'if the month need is at or below the limit'],
   ['batchLarge', 'Big batch', 'The batch of a packet whose month need is above the limit. A bigger shortage takes a whole number of such batches: 7,000 missing is 2 batches, 13,000 is 3', 'if the month need is above the limit']],
  [['batchThreshold', 'Month need limit', 'The limit between the small and the big batch. The month need of a packet is what the plan takes of it from today for as many days as the current month has (30 or 31)', 'month need = from today, 30 or 31 days ahead'], null],
  [['yellowMax', 'Yellow: balance at or below', 'A packet in the plan with a balance from the red limit up to this number is yellow'],
   ['redBelow', 'Red: balance below', 'A packet in the plan with a balance below this number is red and counts in SKUs short; it gets a batch in To produce on the day it drops below']],
];
const WEEK = [1, 2, 3, 4, 5, 6, 0];   // Monday first; values are JS weekdays (0 = Sunday)
const SettingsModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.flat().filter(Boolean).map(([k]) => [k, String(initial[k])])));
  const [days, setDays] = useState(initial.workDays);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const downOnOverlay = useRef(false);   // close only when mousedown AND click land on the overlay

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await api.put('/plantoproduce/packets-need/settings', { ...form, workDays: days });
      onSaved(r.data.settings);
    } catch (e) {
      console.error('Error saving packets need settings:', e);
      setErr(errMsg(e, 'Failed to save'));
      setSaving(false);
    }
  };

  return (
    <div className="pp-modal-overlay"
         onMouseDown={e => { downOnOverlay.current = e.target === e.currentTarget; }}
         onClick={e => { if (downOnOverlay.current && e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="pp-modal">
        <div className="pp-modal-title">Packets Need settings</div>
        {FIELDS.map((pair, i) => (
          <div className="pp-modal-row" key={i}>
            {pair.map((f, j) => (f
              ? <label key={f[0]} title={f[2]}>{f[1]}<input type="number" min="0" inputMode="numeric" value={form[f[0]]} autoFocus={i === 0 && j === 0} onChange={e => setForm(s => ({ ...s, [f[0]]: e.target.value }))} />{f[3] && <span className="pn-hint">{f[3]}</span>}</label>
              : <label key={j} />))}
          </div>
        ))}
        <div className="pn-days" title="The shifts are compared with the working days of the period; days off are greyed in the table">
          <div>Working days <span className="pp-muted">({days.length} per week)</span></div>
          {WEEK.map(d => (
            <label key={d}><input type="checkbox" checked={days.includes(d)} onChange={() => setDays(x => toggle(x, d))} />{DAY[d]}</label>
          ))}
        </div>
        {err && <div className="pp-modal-err">{err}</div>}
        <div className="pp-modal-actions">
          <button className="pp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pp-btn pp-btn-primary" onClick={save} disabled={saving || !days.length} title={days.length ? undefined : 'Choose at least one working day'}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const PacketsNeedTab = () => {
  const curYM = toMiamiDateString(new Date()).slice(0, 7);
  const [view, setView] = useState(() => loadView(curYM));
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const reqRef = useRef(0);   // drops a stale response after a fast range change

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(view)); } catch {} }, [view]);
  const patch = useCallback(p => setView(v => ({ ...v, ...p })), []);

  const { to } = view;
  const load = useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/plantoproduce/packets-need', { params: { to } });
      if (id === reqRef.current) setData(r.data);
    } catch (err) {
      if (id !== reqRef.current) return;
      console.error('Error loading packets need:', err);
      setError(errMsg(err, 'Failed to load the packets need'));
    } finally { if (id === reqRef.current) setLoading(false); }
  }, [to]);
  useEffect(() => { load(); }, [load]);

  const today = data ? data.today : toMiamiDateString(new Date());
  // workDays default: a server that does not know the setting yet (the minute between the client build and the backend restart)
  const settings = useMemo(() => (data ? { workDays: [1, 2, 3, 4, 5, 6], ...data.settings } : null), [data]);
  // The report starts today: the elapsed days of the current month are cut out of the calendar
  // (the week that has already begun keeps its remaining days and gets the '*' of a partial week).
  const cal = useMemo(() => ({
    months: buildCal(curYM, to).months.map(({ m, segs }) => ({
      m,
      segs: segs.map(s => {
        const dates = s.dates.filter(d => d >= today);
        return dates.length === s.dates.length ? s : { ...s, dates, partial: true, label: s.label.replace(/\*?$/, '*') };
      }).filter(s => s.dates.length),
    })).filter(x => x.segs.length),
  }), [curYM, to, today]);
  const workDays = settings ? settings.workDays : WEEK;   // nothing is greyed until the settings arrive
  const model = useMemo(() => buildModel(cal, view.expandedMonths, view.collapsedWeeks, today, workDays, view.balance), [cal, view.expandedMonths, view.collapsedWeeks, today, workDays, view.balance]);
  const monthOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => addMonths(curYM, i)), [curYM]);

  // One entry per packet: its sparse maps + the balance chain from today to the end of the range
  const rows = useMemo(() => {
    if (!data) return [];
    const dates = cal.months.flatMap(({ segs }) => segs.flatMap(s => s.dates));   // today … the end of the range
    return data.packets.map(p => {
      const need = data.need[p.SKU] || EMPTY, planned = data.planned[p.SKU] || EMPTY;
      let total = 0;
      for (const [d, q] of Object.entries(need)) if (d >= data.today) total += num(q);
      // monthNeed: the need of the month that starts today picks the batch ({} from a server that does not send it yet)
      const chain = packetChain({ stock: p.stock, need, planned, todayLeft: data.todayLeft[p.SKU], monthNeed: (data.monthNeed || EMPTY)[p.SKU], dates, settings: data.settings });
      return { p, need, planned, chain, active: total > 0 || Object.keys(planned).length > 0 };
    });
  }, [data, cal]);

  // Packets with neither a need nor planned production in the range are hidden unless asked for
  const idle = useMemo(() => rows.filter(r => !r.active).length, [rows]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => (view.showAll || r.active) && (!q || r.p.name.toLowerCase().includes(q) || r.p.SKU.toLowerCase().includes(q)));
  }, [rows, view.showAll, search]);
  // Balance view: the summary of a column covers today … the last day of the column (running totals).
  // ponytail: sums every column from today again; prefix sums per packet if a long range gets slow.
  const allDates = useMemo(() => cal.months.flatMap(({ segs }) => segs.flatMap(s => s.dates)), [cal]);
  const sums = useMemo(() => {
    if (!settings) return [];
    const at = Object.fromEntries(allDates.map((d, i) => [d, i]));
    return model.cols.map(c => (!c.dates ? null
      : periodSummary(visible, view.balance ? allDates.slice(0, at[c.dates[c.dates.length - 1]] + 1) : c.dates, settings)));
  }, [model.cols, visible, settings, view.balance, allDates]);

  const onHead = act => setView(v => (act.type === 'month'
    ? { ...v, expandedMonths: toggle(v.expandedMonths, act.m) }
    : { ...v, collapsedWeeks: toggle(v.collapsedWeeks, act.week) }));

  return (
    <div className="pn-root">
      <div className="pp-bar">
        <input type="search" className="pn-search" placeholder="Search by name or SKU…" value={search} onChange={e => setSearch(e.target.value)} />
        <span>Through</span>
        <select value={to} onChange={e => patch({ to: e.target.value })}>
          {monthOptions.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <span className="pp-sep" />
        <button className={`pp-tgl${view.balance ? '' : ' on'}`} onClick={() => patch({ balance: false })} title="Per period: how many packets the plan takes in the day, week or month, and what has to be produced in it">Need</button>
        <button className={`pp-tgl${view.balance ? ' on' : ''}`} onClick={() => patch({ balance: true })} title="At the end of the period: the balance (stock + planned production − need) and the totals counted from today to that day">Balance</button>
        <span className="pp-sep" />
        <button className={`pp-tgl${view.showAll ? ' on' : ''}`} onClick={() => patch({ showAll: !view.showAll })} title="Packets with neither a need nor planned production in the range">Packets without need <span className="pp-muted">({idle})</span></button>
        <span className="pp-sep" />
        <button className="pp-tgl" onClick={() => patch({ expandedMonths: [] })}>Collapse all months</button>
        <button className="pp-tgl" onClick={() => patch({ expandedMonths: view.expandedMonths.includes(curYM) ? view.expandedMonths : [...view.expandedMonths, curYM] })}>Expand current month</button>
        <span className="pp-sep" />
        <button className="pp-tgl" onClick={() => setSettingsOpen(true)} disabled={!settings} title="Capacity per day, batches, colour thresholds"><i className="fas fa-cog"></i> Settings</button>
        <button className="pp-tgl" onClick={load} disabled={loading}><i className="fas fa-sync-alt"></i> Refresh</button>
        {loading && <span className="pp-muted"><i className="fas fa-circle-notch spin"></i> Loading…</span>}
      </div>

      {error && (
        <div className="ptp-load-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
          <button className="pp-x" onClick={() => setError(null)} title="Dismiss">×</button>
        </div>
      )}

      <div className="pn-wrap">
        <table className="pp-table" style={{ width: model.width }}>
          <colgroup>{model.cols.map((c, i) => <col key={i} style={{ width: c.w }} />)}</colgroup>
          <thead>
            {model.rows.map((r, ri) => (
              <tr key={ri}>
                {r.map(h => (
                  <th key={h.key} colSpan={h.colSpan} rowSpan={h.rowSpan} title={h.title}
                      className={`${h.cls || ''}${h.act ? ' pp-click' : ''}`}
                      onClick={h.act ? () => onHead(h.act) : undefined}>
                    {h.label}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {settings && (view.balance ? SUM_ROWS_TOTAL : SUM_ROWS).map(([kind, label, title], si) => (
              <tr key={kind} className="pn-sum">
                <td className="pp-name" style={{ top: ROW_H * (3 + si) }} title={title}>{label}</td>
                {model.cols.slice(1).map((c, i) => {
                  const x = c.dates ? sumCell(kind, sums[i + 1], settings, view.balance) : { text: '' };
                  return <td key={i} className={`${c.cls || ''} ${x.cls || ''}`} style={{ top: ROW_H * (3 + si) }} title={x.title || title}>{x.text}</td>;
                })}
              </tr>
            ))}
            <tr className="pp-group">
              <td className="pp-glabel">
                Packets <span className="pp-muted">({visible.length}{search.trim() ? ' found' : ''})</span>
                <button className="pp-gbtn" title={view.listCollapsed ? 'Expand the list' : 'Collapse the list'} onClick={() => patch({ listCollapsed: !view.listCollapsed })}>{view.listCollapsed ? '+' : '−'}</button>
              </td>
              <td colSpan={model.cols.length - 1} />
            </tr>
            {!view.listCollapsed && visible.map(r => <Row key={r.p.SKU} r={r} cols={model.cols} balance={view.balance} settings={settings} />)}
            {!view.listCollapsed && visible.length === 0 && (
              <tr><td className="pp-empty" colSpan={model.cols.length}>{loading ? 'Loading…' : data ? 'No packets match' : ''}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {settingsOpen && (
        <SettingsModal initial={settings} onClose={() => setSettingsOpen(false)}
                       onSaved={s => { setData(d => ({ ...d, settings: s })); setSettingsOpen(false); }} />
      )}
    </div>
  );
};

export default PacketsNeedTab;
